/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';

import MuiToggleButton, { ToggleButtonProps as MuiToggleButtonProps } from '@material-ui/lab/ToggleButton';
import MuiToggleButtonGroup, { ToggleButtonGroupProps as MuiToggleButtonGroupProps } from '@material-ui/lab/ToggleButtonGroup';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */


import { FieldRenderProps } from 'react-final-form';
import LookupContext from '../../../context/LookupContext';
import { ILookup } from '@ngt/online-patient-management-ui';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */


type ToggleButtonGroupProps = Partial<MuiToggleButtonGroupProps> & FieldRenderProps<any, any>

interface IToggleButtonGroup extends ToggleButtonGroupProps {
    toggleButtonProps?: MuiToggleButtonProps;
    lookup?: ILookup 
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    group: {
        width: '100%'
    },
    button: {
        width: '100%',
        height: 32
    }
}));


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const ToggleButtonGroup: React.FunctionComponent<IToggleButtonGroup> = ({
    input: { name, value, onChange: onInputChange, checked, onBlur, onFocus, multiple, type },
    meta,
    onChange,
    children,
    exclusive,
    size,
    toggleButtonProps,
    disabled,
    className,
    lookup,
    ...rest
}) => {
    const classes = useStyles();

    const lookupContext = React.useContext(LookupContext);

    const onChangeCombined = React.useCallback((event: React.MouseEvent<HTMLElement>, eventValue: any) => {
        if (onChange) {
            onChange(event, eventValue);
        }

        onInputChange(eventValue);
    }, [onChange, onInputChange]);

    return (
        <MuiToggleButtonGroup
            {...rest as any}
            name={name}
            exclusive={exclusive === undefined ? true : exclusive}
            size={size === undefined ? 'small' : size}
            value={value}
            onChange={onChangeCombined}
            onFocus={onFocus}
            onBlur={onBlur}
            className={className ?? classes.group}
        >
            {
                children ?? (lookup?.items ?? lookupContext?.items)?.map(item => {
                    return (
                        <MuiToggleButton
                            {...toggleButtonProps}
                            disabled={disabled === true ? true : toggleButtonProps?.disabled}
                            key={item.id}
                            value={item.id}
                            className={toggleButtonProps?.className ?? classes.button}
                        >
                            {item.value}
                        </MuiToggleButton>
                    );
                })
            }
        </MuiToggleButtonGroup>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default ToggleButtonGroup;
